import { Link } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Avatar } from "@/components/ui/Avatar";
import { DownloadableImage } from "@/components/ui/DownloadableImage";
import { MentionText } from "@/components/ui/MentionText";
import { fromTimestamp } from "@/lib/firestore";
import { isEdited } from "@/lib/postEdit";
import { relativeDate } from "@/lib/humanDate";
import { useTranslations } from "@/contexts/I18nContext";
import type { HouseholdPost, MemberProfile } from "@/lib/models";

interface PostCardCompactProps {
  post: HouseholdPost;
  author?: MemberProfile;
  members: MemberProfile[];
}

const REACTION_EMOJI: Record<string, string> = {
  love: "❤️",
  haha: "😂",
  celebrate: "🎉",
  wow: "😮",
  sad: "😢",
  fire: "🔥",
};

/**
 * Compact post card used on the Dashboard "Recent posts" rail. Shows
 * the same content density as iOS `PostCardView` reused on the
 * dashboard (`Dashboard.swift:1334`) — author + title + body preview
 * + image thumbnails + reaction pills + reply count + edited badge —
 * but without the row-action menu, since the dashboard isn't a
 * primary editing surface.
 *
 * Posts.tsx keeps its own (richer) row-with-actions render; this
 * component closes the parity gap on Dashboard alone without
 * disturbing the feed implementation.
 */
export function PostCardCompact({ post, author, members }: PostCardCompactProps) {
  const t = useTranslations();
  const date = fromTimestamp(post.created_at);
  const reactionEntries = Object.entries(post.reactions ?? {}).filter(
    ([, users]) => users.length > 0,
  );

  return (
    <Link to={`/posts/${post.uid}`} className="block">
      <Card className="mb-2">
        <div className="flex items-start gap-3">
          <Avatar
            src={author?.profile_url}
            alt={author?.nickname ?? author?.name ?? ""}
            size={32}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold text-sm truncate">
                {post.post_title}
              </p>
              <div className="flex items-center gap-1.5 shrink-0">
                {post.is_pinned && (
                  <span
                    title={t("posts.pinnedBadge")}
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                  >
                    📌
                  </span>
                )}
                {date && (
                  <span className="text-xs text-gray-400">
                    {relativeDate(date)}
                    {isEdited(post) && (
                      <>
                        {" "}
                        <span className="italic">{t("posts.edited")}</span>
                      </>
                    )}
                  </span>
                )}
              </div>
            </div>
            {post.post_message && (
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                <MentionText text={post.post_message} members={members} />
              </p>
            )}

            {post.images && post.images.length > 0 && (
              <div className="mt-1.5 grid grid-cols-3 gap-1">
                {post.images.slice(0, 3).map((url, i) => (
                  <DownloadableImage
                    key={i}
                    src={url}
                    alt={t("posts.detail.imageAlt", { n: i + 1 })}
                    className="rounded-md overflow-hidden max-h-20"
                  />
                ))}
              </div>
            )}

            {(reactionEntries.length > 0 || post.num_replies > 0) && (
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {reactionEntries.map(([emoji, users]) => (
                  <span
                    key={emoji}
                    className="inline-flex items-center gap-0.5 text-[11px] text-gray-500"
                  >
                    {REACTION_EMOJI[emoji] ?? emoji} {users.length}
                  </span>
                ))}
                {post.num_replies > 0 && (
                  <span className="text-[11px] text-gray-400">
                    {t(
                      post.num_replies === 1
                        ? "posts.replies.one"
                        : "posts.replies.other",
                      { count: post.num_replies },
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>
    </Link>
  );
}
